/**
 * Self-hosted non-Latin web fonts (declared as @font-face in index.html, files in public/fonts/).
 * No third-party font requests — see specs/word-mastery-progression.md / store compliance.
 *
 * Latin languages (de/en/fr/es) need no extra font (system-ui covers them). Only Thai/JP/KR
 * require a real face for correct shaping (e.g. Thai tone marks), so we fetch just the one
 * matching the active language — keeping the large CJK files off Latin users' devices.
 *
 * Thai uses Sarabun rather than Noto Sans Thai: it is the modernised form of Thailand's standard
 * document font, so it is the letter shape Thai children meet in print, its head loops are more
 * open at the game's text size, and it renders larger per px. See wordTextStyle() below — the
 * face is only half of what makes Thai readable here.
 */
import { FONT_STACK } from '@/game/config';

const FAMILY_FOR_LANG: Record<string, string> = {
  th: 'Sarabun',
  ja: 'Noto Sans JP',
  ko: 'Noto Sans KR',
};

/** The stroke/shadow half of a flying word's style — see wordTextStyle. */
export interface WordTextStyle {
  fontStyle?: string;
  stroke: string;
  strokeThickness: number;
  shadow?: { offsetX: number; offsetY: number; color: string; blur: number; stroke: boolean; fill: boolean };
}

/**
 * Script-dependent styling for the flying word labels (BoardScene, SurvivalScene).
 *
 * Latin words are drawn white with a heavy 6px outline: it reads as arcade lettering and holds
 * the word legible over the starfield. Thai cannot take that outline. Its letters are told apart
 * largely by small head loops (ก/ถ, ค/ด, บ/ษ, พ/ผ) and it stacks tone marks above and below the
 * base letter — and a canvas stroke grows inward as well as outward, so at 40px a 6px outline
 * fills the loops in and closes the few pixels between a mark and its base. Letters that differ
 * only by a loop start to converge, which is the opposite of what a reading app should do.
 *
 * Thai therefore gets a thin outline with a drop shadow carrying the contrast instead, and bold
 * so the strokes stay solid — the canvas is 1280 wide scaled to fit, so 40px lands near 28 device
 * px on a phone in landscape.
 */
export function wordTextStyle(lang: string): WordTextStyle {
  if (lang.split('-')[0] !== 'th') {
    return { stroke: '#000000', strokeThickness: 6 };
  }
  return {
    fontStyle: 'bold',
    stroke: '#000000',
    strokeThickness: 2,
    shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 6, stroke: true, fill: true },
  };
}

/**
 * The font stack for a language's script, with no arcade styling attached.
 *
 * `wordTextStyle` above is for words flying over a photo backdrop, so it carries a heavy 6px
 * outline; on a light plate — the Ice Maze's letter tiles — that same outline reads as a fat,
 * over-inked letter and makes a grid of them look like a wall. This gives the face alone, so text
 * on a plate can be drawn plain and still be shaped correctly in Thai, Japanese and Korean.
 */
export function scriptFontFamily(lang: string): string {
  const family = FAMILY_FOR_LANG[lang.split('-')[0]];
  return family ? `"${family}", ${FONT_STACK}` : FONT_STACK;
}

/** Load (if needed) the web font for a language's script. Resolves once ready or skipped. */
export async function ensureScriptFont(lang: string): Promise<void> {
  if (!('fonts' in document)) return;
  const family = FAMILY_FOR_LANG[lang.split('-')[0]];
  if (!family) return;
  try {
    await Promise.all([
      document.fonts.load(`400 16px "${family}"`),
      document.fonts.load(`700 16px "${family}"`),
    ]);
  } catch (err) {
    console.warn(`[fonts] ${family} load failed; falling back to system fonts.`, err);
  }
}
